<template>
  <div
    class="supplement-sidebar no-print"
    role="complementary"
    aria-label="Supplement Editor Tools"
  >
    <div
      v-if="store.isConflicted || store.isOutOfSync"
      class="notification is-danger conflict-banner mb-4"
      role="alert"
    >
      <p class="mb-2">
        <strong><i class="fas fa-exclamation-triangle" aria-hidden="true"></i> Out of Sync</strong>
      </p>
      <p class="is-size-7 mb-3">
        This label was modified in another session. Your changes cannot be saved until you reload.
      </p>
      <button
        class="button is-small is-danger is-outlined"
        :disabled="isReloading"
        @click="handleReload"
      >
        <span v-if="isReloading" class="icon is-small"><i class="fas fa-spinner fa-spin"></i></span>
        <span v-else class="icon is-small"><i class="fas fa-sync-alt"></i></span>
        <span>{{ isReloading ? "Reloading..." : "Reload Latest" }}</span>
      </button>
    </div>

    <ServingSection />
    <DietaryInformationSection @open-recipe-import="$emit('open-recipe-import')" />
    <IngredientsWithDvSection />
    <IngredientsWithoutDvSection />
    <BlendsSection />
    <SettingsSection />

    <div
      class="save-status mt-4 has-text-centered"
      :class="{ 'is-invisible': !store.isSaving && !store.error && !showSaveSuccess }"
      aria-live="polite"
      aria-atomic="true"
    >
      <span v-if="store.isSaving" class="has-text-grey">
        <i class="fas fa-spinner fa-spin" aria-hidden="true"></i> Saving...
      </span>
      <span v-else-if="store.error" class="has-text-danger is-size-7">
        <i class="fas fa-exclamation-triangle" aria-hidden="true"></i> {{ store.error }}
        <button
          v-if="store.canRetry"
          class="button is-small is-danger is-outlined ml-2"
          @click="store.retrySave"
        >
          <span class="icon is-small"><i class="fas fa-redo"></i></span>
          <span>Retry</span>
        </button>
      </span>
      <span v-else-if="showSaveSuccess" class="has-text-success">
        <i class="fas fa-check" aria-hidden="true"></i> Saved
      </span>
      <span v-else>&nbsp;</span>
    </div>
  </div>
</template>

<script setup>
  import { ref, watch, onMounted, onUnmounted } from "vue";
  import { useSupplementLabelStore } from "./stores/supplement-label-store";
  import ServingSection from "./supplement-editor/serving-section.vue";
  import DietaryInformationSection from "./supplement-editor/dietary-information-section.vue";
  import IngredientsWithDvSection from "./supplement-editor/ingredients-with-dv-section.vue";
  import IngredientsWithoutDvSection from "./supplement-editor/ingredients-without-dv-section.vue";
  import BlendsSection from "./supplement-editor/blends-section.vue";
  import SettingsSection from "./supplement-editor/settings-section.vue";

  const emit = defineEmits(["open-recipe-import"]);
  const store = useSupplementLabelStore();
  const isReloading = ref(false);
  const showSaveSuccess = ref(false);
  let saveSuccessTimeout = null;

  watch(
    () => store.isSaving,
    (isSaving, wasSaving) => {
      if (wasSaving && !isSaving && !store.error) {
        showSaveSuccess.value = true;
        if (saveSuccessTimeout) clearTimeout(saveSuccessTimeout);
        saveSuccessTimeout = setTimeout(() => {
          showSaveSuccess.value = false;
        }, 2000);
      }
    }
  );

  const handleReload = async () => {
    isReloading.value = true;
    await store.reloadFromServer();
    isReloading.value = false;
  };

  function handleImportButtonClick() {
    emit("open-recipe-import");
  }

  onMounted(() => {
    const importBtn = document.getElementById("import-from-recipe-btn");
    if (importBtn) {
      importBtn.addEventListener("click", handleImportButtonClick);
    }
  });

  onUnmounted(() => {
    const importBtn = document.getElementById("import-from-recipe-btn");
    if (importBtn) {
      importBtn.removeEventListener("click", handleImportButtonClick);
    }
    if (saveSuccessTimeout) {
      clearTimeout(saveSuccessTimeout);
    }
  });
</script>

<style lang="scss" scoped>
  .conflict-banner {
    position: sticky;
    top: 0;
    z-index: 10;
    border-radius: 4px;
  }
</style>
